/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useEffect, useState } from 'react'
import req from '@sportninja/common/api/request'
import { t } from '@sportninja/common/i18n'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import { ENTITY_TYPES } from '@sportninja/common/sagas/utils'
import parsers from '@sportninja/common/reducers/parsers'
import { ENABLE_REGISTRATION, ROUTES } from '@sportninja/common/constants/app'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

import { scheduleForm } from '../../components/Form/form-configs'
import { Flex } from '../../components/Layout'
import Picture from '../../components/Picture'
import useGetListItems from '../../components/List/useGetListItems'
import OptionButtonEdit from '../../components/List/OptionButtons/Edit'
import OptionButtonDelete from '../../components/List/OptionButtons/Delete'
import OptionButtonFavourite from '../../components/List/OptionButtons/Favourite'
import List from '../../components/List'
import EntityFormDelete from '../../components/Form/EntityFormDelete'
import EditEntitySubTitle from '../EditEntitySubTitle'
import MoreOptions, {
  MoreOptionListItem,
} from '../../components/List/OptionButtons/More'
import colors from '@sportninja/common/constants/appColors'
import CloneSchedule from '../Schedule/CloneSchedule'
import Icon from '../../components/Icon'
import EditRegistration from '../Schedule/EditRegistration'
import { isCanlan } from '@sportninja/common/utils/customer-name'

dayjs.extend(utc)

const ScheduleCell = ({ item }) => (
  <Flex alignItems='center'>
    <Picture
      size='kxsmall'
      square
      fit
      imageId={getImageThumbnailId(item)}
      iconName={item.is_tournament ? 'trophy' : 'calendar-alt'}
      css={css`
        margin-right: 8px;
      `}
    />
    {item.name_full}
  </Flex>
)

const ScheduleList = ({
  refreshKey,
  setCount = () => {},
  showParentColumn,
  parentId,
  accountTypeId,
  slug,
  defaultSort,
  defaultDirection,
  syncSalesforce,
  soccerSport,
  hockeySport,
  orgFeatures,
}) => {
  const [filter, setFilter] = useState('')
  const listProps = useGetListItems(slug, {
    sortDefault: defaultSort,
    directionDefault: defaultDirection,
    onComplete: (response) => setCount(response.meta.pagination.count),
  })

  const {
    sort,
    direction,
    getItems,
    permissions,
    debouncedSearchTerm,
    features,
  } = listProps

  useEffect(() => {
    getItems()
  }, [refreshKey, sort, direction, debouncedSearchTerm])

  useEffect(() => {
    if (filter === '') {
      getItems()
    } else {
      getItems(1, { sport_id: filter })
    }
  }, [filter])

  const headers = [
    {
      text: t('common:name'),
      sizePercent: 30,
      minWidth: 176,
      sort: 'name_full',
      Cell: ScheduleCell,
      to: (item) => ({
        pathname: `${ROUTES.SCHEDULE_ROOT}/${item.id}`,
        state: { fromParent: parentId },
      }),
    },
    showParentColumn && {
      text: t('common:parentOrg'),
      sizePercent: 30,
      sort: 'organization.name_full',
      accessor: 'organization.name_full',
      to: (item) => `${ROUTES.ORG_ROOT}/${item.organization.id}`,
    },
    {
      text: 'Sport',
      sizePercent: 15,
      minWidth: 60,
      sort: 'sport_id',
      accessor: (item) =>
        item?.sport_id === soccerSport?.id
          ? soccerSport?.name_full
          : hockeySport?.name_full || '-',
    },
    {
      text: t('common:game', { count: 2 }),
      sizePercent: 5,
      minWidth: 60,
      sort: 'games_count',
      accessor: 'games_count',
    },
    {
      text: t('common:start'),
      sizePercent: 14,
      minWidth: 100,
      sort: 'starts_at',
      accessor: (item) => dayjs.utc(item.starts_at).format("MMM DD, 'YY"),
    },
    {
      text: t('common:end'),
      sizePercent: 14,
      minWidth: 100,
      sort: 'ends_at',
      accessor: (item) => dayjs.utc(item.ends_at).format("MMM DD, 'YY"),
    },
    (ENABLE_REGISTRATION &&
      !isCanlan &&
      orgFeatures?.registration &&
      accountTypeId >= 2) && {
        text: 'REGIST. STATUS',
        sizePercent: 14,
        minWidth: 100,
        sort: 'reg_status',
        accessor: (item) => {
          return (
            <div
              css={css`
                display: flex;
                align-items: center;
                background-color: ${item.online_registration_open
                  ? colors.SUCCESS_DARK
                  : colors.SOFT_STEEL};
                border-radius: 16px;
                padding: 8px 16px;
                gap: 8px;
              `}
            >
              <Icon
                name={
                  item.online_registration_open
                    ? 'check-circle'
                    : 'minus-circle'
                }
                fontSize={13}
                faType='far'
              />
              <span
                css={css`
                  color: ${colors.WHITE};
                  font-size: 16x;
                `}
              >
                {item.online_registration_open ? 'Open' : 'Non-set'}
              </span>
            </div>
          )
        },
      },
  ]

  const options = (item) => {
    const shouldSyncSalesforce =
      syncSalesforce || item?.organization?.sync_salesforce
    const permission = permissions[item.id]

    const onEdit = async (...args) => {
      const [values] = args
      const { imageData, ...form } = values

      const shouldDeleteImage = values?.delete_image ?? false

      const body = JSON.stringify(form)
      const response = await req(`/schedules/${item.id}`, {
        method: 'PUT',
        body,
      })

      if (shouldDeleteImage) {
        await req(`/schedules/${item.id}/image`, {
          method: 'DELETE',
        })
      }

      if (imageData) {
        await req(`/schedules/${item.id}/image`, {
          method: 'POST',
          body: imageData,
        })
      }
      await getItems()
      return response
    }

    const opts = [
      item.online_registration_open &&
        orgFeatures?.registration &&
        ENABLE_REGISTRATION &&
        permission.update &&
        accountTypeId >= 2 && (
          <EditRegistration
            key='edit-registration'
            scheduleId={item.id}
            removeBorder
            triggerReload
          />
        ),
      permission.update && (
        <OptionButtonEdit
          key='edit'
          form={scheduleForm(
            parsers[ENTITY_TYPES.schedule](item),
            false,
            shouldSyncSalesforce,
            hockeySport?.id,
            (isCanlan || orgFeatures?.tournaments)
          )}
          onSubmit={onEdit}
          title={t('Web:editCompetition')}
          subTitle={
            <EditEntitySubTitle
              id={item.id}
              entityType={ENTITY_TYPES.schedule}
            />
          }
          Delete={
            permission.delete && (
              <EntityFormDelete
                entityType={ENTITY_TYPES.schedule}
                itemId={item.id}
                title={`Delete ${item.name_full}`}
                type={t('common:competition')}
                onComplete={getItems}
              />
            )
          }
        />
      ),
      <OptionButtonFavourite
        key='fav'
        entityType={ENTITY_TYPES.schedule}
        item={item}
        isFavourite={item.is_favorite}
      />,
      <MoreOptions
        key='more'
        options={[
          permission.update && {
            key: 'clone',
            Component: CloneSchedule,
            componentProps: {
              shouldEnablePlayoffs: (isCanlan || orgFeatures?.tournaments),
              Button: ({ onClick, onToggle }) => {
                return (
                  <MoreOptionListItem
                    onClick={() => {
                      onClick()
                      onToggle()
                    }}
                    iconName='clone'
                    faType='far'
                    title={`Clone ${t('common:competition')}`}
                  />
                )
              },
              schedule: item,
              scheduleId: item.id,
            },
          },
          permission.delete && {
            key: 'delete',
            Component: OptionButtonDelete,
            componentProps: {
              useForceDelete: true,
              unmountOnClose: true,
              entityType: ENTITY_TYPES.schedule,
              itemId: item.id,
              title: `Delete ${item.name_full}`,
              type: t('common:competition'),
              iconName: 'trash',
              onComplete: getItems,
            },
          },
        ]}
      />,
    ]

    return opts.filter((o) => o !== undefined)
  }

  return (
    <List
      headers={headers.filter((o) => o !== undefined)}
      options={options}
      listProps={listProps}
      noItemsText={t('common:competition', { count: 2 })}
      showSearch
      sportFilterOptions={[
        { text: 'All', value: '' },
        { text: soccerSport?.name_full, value: soccerSport?.id },
        { text: hockeySport?.name_full, value: hockeySport?.id },
      ]}
      sportFilter={filter}
      setSportFilter={setFilter}
      showSportFilter={true}
    />
  )
}

export default ScheduleList
